/* eslint-disable */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { openAuthenticatedPdf } from "../../lib/pdfDownloader";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Mail, Download, Plus, Trash2, FileText, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

const CHANNEL_LABEL = {
  email: { label: "Email", icon: <Mail className="w-3 h-3" />, className: "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]" },
  postal: { label: "Courrier postal", icon: <Send className="w-3 h-3" />, className: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  autre: { label: "Autre", icon: <FileText className="w-3 h-3" />, className: "bg-[#F0F0EB] text-[#575A57] border-[#E5E5E0]" },
};

/**
 * Section "Correspondance" affichée sur la fiche d'un revendeur.
 * Permet de créer / lister / télécharger en PDF / archiver les correspondances
 * envoyées (email, postal, autre) au format corporate Maison Thaï.
 */
export default function CorrespondenceSection({ resellerId, resellerName }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", channel: "email", sent_at: "" });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfId, setPdfId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/correspondences?reseller_id=${resellerId}`);
      setItems(data || []);
    } catch (e) {
      toast.error("Impossible de charger les correspondances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (resellerId) load(); }, [resellerId]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ subject: "", body: "", channel: "email", sent_at: "" });
    setDialogOpen(true);
  };

  /**
   * Pré-remplit l'objet et le corps avec la lettre type d'accompagnement
   * des nouvelles dispositions tarifaires. L'utilisateur peut ensuite
   * personnaliser librement avant d'enregistrer.
   */
  const applyTariffTemplate = () => {
    const year = new Date().getFullYear();
    setForm((f) => ({
      ...f,
      subject: `Nouvelles dispositions tarifaires applicables — ${year}`,
      body:
        `Madame, Monsieur,\n\n` +
        `Dans le cadre de notre partenariat, nous avons le plaisir de vous communiquer ` +
        `les nouvelles dispositions tarifaires applicables à votre point de vente.\n\n` +
        `Vous trouverez en annexe le document « Tarifs ${resellerName} » détaillant ` +
        `pour chaque référence :\n` +
        `  • le prix revendeur net (incluant votre remise contractuelle),\n` +
        `  • le prix public conseillé,\n` +
        `  • la marge associée.\n\n` +
        `Ces conditions s'appliquent à compter de leur date d'envoi et remplacent ` +
        `toute grille antérieure. Nous restons naturellement à votre disposition pour ` +
        `toute question, ajustement ou négociation complémentaire.\n\n` +
        `En vous remerciant de votre confiance et du soin apporté à la mise en valeur ` +
        `de nos plats,`,
    }));
    toast.success("Modèle « Nouveau tarif » appliqué — personnalisez librement");
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      subject: it.subject || "",
      body: it.body || "",
      channel: it.channel || "email",
      sent_at: it.sent_at ? it.sent_at.split("T")[0] : "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.subject.trim()) {
      toast.error("L'objet est obligatoire");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        reseller_id: resellerId,
        subject: form.subject.trim(),
        body: form.body || "",
        channel: form.channel,
        sent_at: form.sent_at ? new Date(form.sent_at).toISOString() : null,
      };
      if (editingId) {
        await api.patch(`/correspondences/${editingId}`, payload);
        toast.success("Correspondance mise à jour");
      } else {
        await api.post(`/correspondences`, payload);
        toast.success("Correspondance créée");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette correspondance ?")) return;
    try {
      await api.delete(`/correspondences/${id}`);
      toast.success("Correspondance supprimée");
      await load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const downloadPdf = async (it) => {
    setPdfId(it.id);
    try {
      await openAuthenticatedPdf(
        `/api/correspondences/${it.id}/pdf`,
        `${it.reference || "correspondance"}.pdf`,
        { mode: "download" }
      );
    } catch (e) {
      toast.error("Téléchargement PDF impossible");
    } finally {
      setPdfId(null);
    }
  };

  const previewPdf = async (it) => {
    setPdfId(it.id);
    try {
      await openAuthenticatedPdf(
        `/api/correspondences/${it.id}/pdf`,
        `${it.reference || "correspondance"}.pdf`,
        { mode: "preview" }
      );
    } catch (e) {
      toast.error("Aperçu PDF impossible");
    } finally {
      setPdfId(null);
    }
  };

  return (
    <section className="bg-white border border-[#E5E5E0] rounded-lg p-5 lg:col-span-3">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-heading text-base flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#8C6B2F]" /> Correspondance
          <span className="text-xs text-[#A3A6A3] font-normal">({items.length})</span>
        </h3>
        <Button size="sm" variant="outline" onClick={openCreate} data-testid="corr-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nouvelle lettre
        </Button>
      </div>
      <p className="text-xs text-[#575A57] mb-3">
        Suivi des courriers (email, postal) envoyés à <strong>{resellerName}</strong> — chaque entrée
        peut être téléchargée en PDF format corporate Maison Thaï.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-[#A3A6A3]">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#A3A6A3] italic text-center py-6">
          Aucune correspondance enregistrée. Cliquez « Nouvelle lettre » pour démarrer le suivi.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const ch = CHANNEL_LABEL[it.channel] || CHANNEL_LABEL.autre;
            return (
              <div
                key={it.id}
                className="border border-[#E5E5E0] rounded p-3 hover:bg-[#FAFAF8] transition-colors"
                data-testid={`corr-item-${it.id}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-[10px] text-[#A3A6A3]">{it.reference}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${ch.className}`}>
                        {ch.icon} {ch.label}
                      </span>
                      {it.sent_at && (
                        <span className="text-[10px] text-[#5a7a4f]">
                          Envoyé le {fmtDate(it.sent_at)}
                        </span>
                      )}
                      {!it.sent_at && (
                        <span className="text-[10px] text-[#8C6B2F]">Brouillon</span>
                      )}
                    </div>
                    <div className="font-medium text-sm text-[#3D2914]">{it.subject}</div>
                    {it.body && (
                      <div className="text-xs text-[#575A57] mt-1 line-clamp-2 whitespace-pre-line">{it.body}</div>
                    )}
                    <div className="text-[10px] text-[#A3A6A3] mt-1">Créée le {fmtDate(it.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => previewPdf(it)} disabled={pdfId === it.id} title="Aperçu PDF" data-testid={`corr-preview-${it.id}`}>
                      <FileText className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(it)} disabled={pdfId === it.id} title="Télécharger PDF" data-testid={`corr-download-${it.id}`}>
                      {pdfId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(it)} title="Modifier" data-testid={`corr-edit-${it.id}`}>
                      <FileText className="w-3.5 h-3.5 text-[#8C6B2F]" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(it.id)} title="Supprimer" data-testid={`corr-delete-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-[#C05C47]" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="corr-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifier la correspondance" : "Nouvelle correspondance"}</DialogTitle>
            <DialogDescription>
              La page sera mise en forme avec l'en-tête corporate Maison Thaï,
              l'adresse de <strong>{resellerName}</strong> au format suisse, et la date du jour.
              Vous pouvez télécharger le PDF immédiatement après création.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap p-2 bg-[#FBF4E9] border border-[#E8D3A8] rounded">
              <div className="text-xs text-[#8C6B2F]">
                💡 Astuce : cliquez pour appliquer la lettre d'accompagnement
                <strong> nouveau tarif</strong> (modifiable ensuite).
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={applyTariffTemplate}
                data-testid="corr-template-tariff"
                className="bg-white"
              >
                <FileText className="w-3.5 h-3.5 mr-1" /> Modèle : Nouveau tarif
              </Button>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#575A57] uppercase tracking-wider">Objet</label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Ex: Modification des tarifs au 1er juillet 2026"
                data-testid="corr-subject"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#575A57] uppercase tracking-wider">Canal</label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger data-testid="corr-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="postal">Courrier postal</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#575A57] uppercase tracking-wider">Date d'envoi (optionnelle)</label>
                <Input
                  type="date"
                  value={form.sent_at}
                  onChange={(e) => setForm({ ...form, sent_at: e.target.value })}
                  data-testid="corr-sent-at"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#575A57] uppercase tracking-wider">Corps (optionnel)</label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={10}
                placeholder="Rédigez le corps de votre lettre ici. Les sauts de ligne seront conservés. Une salutation « Madame, Monsieur, » est ajoutée automatiquement en début de corps si vous n'en mettez pas, et une formule de politesse en fin de page."
                data-testid="corr-body"
              />
              <p className="text-[10px] text-[#A3A6A3] mt-1">
                Astuce : laissez le corps vide pour générer juste la mise en page (en-tête, destinataire,
                objet, date) et écrire à la main par la suite — utile pour un courrier postal manuscrit.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={save} disabled={saving || !form.subject.trim()} data-testid="corr-save">
              {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Enregistrement…</> : (editingId ? "Mettre à jour" : "Créer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
